import { Bookmaker } from '@/constants';
import { formatElapsed } from '@/helpers/common';
import type { IAccountListRowApi } from '@/types/account';
import { AbstractModel } from './abstractModel';

/**
 * Account grid row — API `snake_case` mapped to `camelCase` in the constructor (same idea as {@link BetItem}).
 */
export class AccountItem extends AbstractModel {
    activeBetsCount: number | null = null;
    apiKey: string | null = null;
    balance: string | null = null;
    betCount: number | null = null;
    browserId: number | null = 1;
    commissionBot: string = '0';
    commissionOrder: string = '0';
    createdAt: string = '';
    currencyId: number | null = null;
    deactivatingStatusId: number | null = null;
    deletedAt: string | null = null;
    errorText: string | null = null;
    id: number = 0;
    isActive: 0 | 1 = 0;
    isDisable: 0 | 1 = 1;
    isLoginedNow: 0 | 1 = 0;
    isSuspendNow: 0 | 1 = 0;
    lastBetAt: string | null = null;
    login: string = '';
    loginedAt: string | null = null;
    logoutedAt: string | null = null;
    maintenanceFrom: string | null = null;
    name: string | null = null;
    password: string = '';
    platformsBot: number[] = [];
    platformsOrder: number[] = [];
    positionPerc: number = 0;
    problem: string | null = null;
    proxyId: number | null = null;
    proxyName: string | null = null;
    proxyPoolIds: number[] = [];
    setName: string | null = null;
    siteId!: Bookmaker;
    suspendTill: string | null = null;
    toolUserIds: number[] | null = [];
    toolUserLogins: string[] | null = null;

    usedForParser: 0 | 1 = 0;
    validPerc: number = 100;

    constructor(data?: IAccountListRowApi) {
        super();

        if (data) {
            this.id = data.id;
            this.siteId = data.site_id;
            this.login = data.login;
            this.name = data.name;
            this.setName = data.set_name;
            this.password = data.password;
            this.apiKey = data.api_key;
            this.proxyId = data.proxy_id;
            this.proxyPoolIds = data.proxy_pool ?? [];
            this.proxyName = data.proxy_name;
            this.browserId = data.browser_id;
            this.currencyId = data.currency_id;
            this.validPerc = data.valid_perc;
            this.commissionBot = data.commission_bot;
            this.commissionOrder = data.commission_order;
            this.positionPerc = data.position_perc;
            this.balance = data.balance;
            this.betCount = data.bet_count;
            this.createdAt = data.created_at;
            this.toolUserLogins = data.tool_user_logins;
            this.toolUserIds = data.tool_user_ids;
            this.usedForParser = data.used_for_parser;
            this.isActive = data.is_active;
            this.isDisable = data.is_disable;
            this.deletedAt = data.deleted_at;
            this.maintenanceFrom = data.maintenance_from;
            this.problem = data.problem;
            this.isSuspendNow = data.is_suspend_now;
            this.suspendTill = data.suspend_till;
            this.errorText = data.error_text;
            this.deactivatingStatusId = data.deactivating_status_id;
            this.activeBetsCount = data.active_bets_count;
            this.logoutedAt = data.logouted_at;
            this.isLoginedNow = data.is_logined_now;
            this.loginedAt = data.logined_at;
            this.platformsBot = data.platforms_bot ?? [];
            this.platformsOrder = data.platforms_order ?? [];
            this.lastBetAt = data.last_bet_at;
        }
    }

    get lastBetHours(): string {
        return formatElapsed(this.lastBetAt);
    }

    get loginHours(): string {
        return formatElapsed(this.loginedAt);
    }

    get logoutHours(): string {
        return formatElapsed(this.logoutedAt);
    }

    /**
     * CSS classes for account grid rows (legacy `AccountGrid` `getRowClass`).
     */
    getGridRowClass(): string {
        const parts: string[] = [];
        const { isActive, isLoginedNow, isDisable } = this;

        if (!isActive || isDisable) {
            parts.push('account-row--muted');
        }
        if (isActive && isLoginedNow) {
            parts.push('account-row--logined');
        } else {
            parts.push('account-row--not-logged');
        }
        if (this.maintenanceFrom || !!this.problem || this.isSuspendNow || isDisable) {
            parts.push('account-row--mark');
        }

        return parts.join(' ');
    }

    isPlatformBot(platformId: number): boolean {
        return this.platformsBot.includes(platformId);
    }

    isPlatformOrder(platformId: number): boolean {
        return this.platformsOrder.includes(platformId);
    }

    /** Soft-deleted (`deleted_at`); relogin and delete are disabled. */
    isSoftDeleted(): boolean {
        return this.deletedAt != null;
    }

    toApiData(): Record<string, any> {
        const isCreate = this.id <= 0;
        const row: Record<string, any> = {
            ...(this.id > 0 ? { id: this.id } : {}),
            site_id: this.siteId,
            login: this.login,
            name: this.name,
            password: this.password || undefined,
            api_key: this.apiKey,
            proxy_id: this.proxyId,
            proxy_pool: this.proxyPoolIds,
            browser_id: this.browserId,
            currency_id: this.currencyId,
            valid_perc: this.validPerc,
            commission_bot: this.commissionBot,
            commission_order: this.commissionOrder,
            position_perc: this.positionPerc,
            priority: 0,
            tool_user_ids: this.toolUserIds,
            used_for_parser: this.usedForParser,
            is_active: isCreate ? 0 : this.isActive,
            is_disable: isCreate ? 1 : this.isDisable,
            platforms_bot: this.platformsBot,
            platforms_order: this.platformsOrder,
        };
        return row;
    }
}
